import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api, type Usuario } from './api/cliente.js';

/**
 * Sessão do painel.
 *
 * Não guardamos token: ele vive em cookie httpOnly. O que este contexto mantém
 * é apenas quem está logado, para decidir o que exibir. A autorização de
 * verdade acontece no servidor — esconder um item de menu é conveniência, não
 * segurança.
 */

interface ContextoSessao {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  ehAdmin: boolean;
}

const Contexto = createContext<ContextoSessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    // Ao abrir o painel, perguntamos ao servidor quem somos. Se o cookie ainda
    // vale, o usuário continua logado sem digitar nada.
    api
      .eu()
      .then((r) => setUsuario(r.usuario))
      .catch((erro) => {
        if (!(erro instanceof ApiError && erro.expirou)) {
          console.error('Falha ao carregar a sessão', erro);
        }
        setUsuario(null);
      })
      .finally(() => setCarregando(false));
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const { usuario: logado } = await api.login(email, senha);
    setUsuario(logado);
  }, []);

  const sair = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setUsuario(null);
  }, []);

  const valor = useMemo<ContextoSessao>(
    () => ({ usuario, carregando, entrar, sair, ehAdmin: usuario?.papel === 'admin' }),
    [usuario, carregando, entrar, sair],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): ContextoSessao {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useSessao precisa estar dentro de ProvedorSessao');
  return contexto;
}
